import { IntersectionV5, IntersectionV6 } from '@api/api/ontology';
import { countDirections } from '@api/utils/ontology-utils';
import { getTimeTillEndOfDayMs } from '@helpers/utils/dates/getTimeTillEndOfDayMs';
import { useQuery } from '@tanstack/react-query';
import { useEarningsFilter } from '@terminal/react-query-hooks/earnings/useEarningsFilter';
import { useEarningsFilterPeriods } from '@terminal/react-query-hooks/earnings/useEarningsFilterPeriods';
import { useEarningsInsightsByEarningsId } from '@terminal/react-query-hooks/insights/useEarningsInsightsByEarningsId';
import { useThemeCompanies } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { postMultipleEntities } from '@terminal/services/entities/entity-service';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import {
  getGraphIntersections,
  getGraphRelations,
  isV6IntersectionsResponse,
} from '@terminal/views/graph/services/graph-service';
import { parseGraphNodeKey } from '@terminal/views/graph/utils/graph-types/graph-utils';
import { getCalendarQuarterInfo } from '@terminal/views/theme-overview/tab-content/earnings/utils/utils';
import {
  PulseBigMover,
  PulseRelatedTheme,
  PulseUpcomingEarning,
} from '@terminal/views/theme-overview/tab-content/pulse-overview/pulse-types';
import { fetchPostFilterPage } from '@terminal/widgets/scenario/hooks/use-screener-entities-tags/entity-screener-service/postFilterQueryOptions';
import { mapEntityTagsToDefaultOptions } from '@terminal/widgets/scenario/hooks/use-screener-entities-tags/useScreenerEntitiesTags';
import { useMemo } from 'react';
import {
  extractOneDayReturns,
  getPulseEarningsStatus,
  pickTopMovers,
  toCompanyRef,
} from '../use-pulse-table/usePulseTables.utils';

const QUERY_KEY_PULSE_BIG_MOVERS = 'QUERY_KEY_PULSE_BIG_MOVERS';
const QUERY_KEY_PULSE_RELATED_THEMES = 'QUERY_KEY_PULSE_RELATED_THEMES';

const ROW_LIMIT = 5;
const BASKET_LIMIT = 75;

export const usePulseBigMovers = ({ themeId }: { themeId: string }) => {
  const companies = useThemeCompanies({
    themeId,
    limit: BASKET_LIMIT,
  });
  const basket = companies.data;

  const movers = useQuery({
    queryKey: [QUERY_KEY_PULSE_BIG_MOVERS, themeId],
    enabled: !!basket && basket.length > 0,
    queryFn: async ({ signal }): Promise<PulseBigMover[]> => {
      const tags = (basket ?? []).map(c => c.company);
      const results = await fetchPostFilterPage({
        requestFilters: mapEntityTagsToDefaultOptions(tags),
        pageNumber: 1,
        pageSize: tags.length,
        signal,
      });

      const ranked = pickTopMovers(extractOneDayReturns(results));

      const entities = await postMultipleEntities(
        ranked.map(([tag]) => tag),
        signal
      );

      const entityByTag = new Map(entities.map(entity => [entity.tag, entity]));
      const rankByTag = new Map(
        (basket ?? []).map(c => [c.company, c.relationship.rank])
      );

      return ranked
        .map(([tag, changePercent]) => ({
          rank: rankByTag.get(tag) ?? 0,
          entity: entityByTag.get(tag),
          changePercent,
          company: toCompanyRef(tag),
        }))
        .filter((mover): mover is PulseBigMover => !!mover.entity);
    },
  });

  return {
    isLoading: companies.isLoading || movers.isLoading,
    data: movers.data,
  };
};

/**
 * Next scheduled earnings within the theme basket: ontology basket ->
 * earnings/v1/filter (same pattern as the Earnings tab), earliest first.
 *
 * Behind KG-976-earnings-filter-v2 this also returns the quarter's reporting
 * `status`, so the section can say why it is empty rather than falling back to
 * generic copy. See getPulseEarningsStatus for the cases it distinguishes.
 */
export const usePulseUpcomingEarnings = ({ themeId }: { themeId: string }) => {
  const companies = useThemeCompanies({
    themeId,
    limit: BASKET_LIMIT,
  });
  const basket = companies.data;
  const isFilterV2 = useFlags(state =>
    state.getFeatureFlagState('KG-976-earnings-filter-v2')
  );

  // UTC day-level dates keep the query key stable and match the Earnings tab quarter.
  const { body, periodsBody, currentQuarter } = useMemo(() => {
    const now = new Date();
    const { startDate, endDate, quarter, year } = getCalendarQuarterInfo(now);
    const todayUtc = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
    );
    const entities = (basket ?? []).map(c => c.company);

    return {
      body: {
        start_date: todayUtc.toISOString(),
        end_date: endDate.toISOString(),
        entities,
      },
      // Status counts span the whole quarter, so this starts at the quarter open
      // rather than today — otherwise companies that already reported are unseen.
      periodsBody: {
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        entities,
      },
      currentQuarter: { quarter, year },
    };
  }, [basket]);

  const earnings = useEarningsFilter({ body, shouldFetchEntities: false });

  const periods = useEarningsFilterPeriods({
    body: periodsBody,
    enabled: isFilterV2 && (basket ?? []).length > 0,
  });

  const upcomingItems = useMemo(() => {
    const items = earnings.data?.pages.flat();
    if (!items) {
      return undefined;
    }

    const seen = new Set<string>();
    const uniqueUpcoming = [];
    const sortedUpcoming = items
      .filter(item => !item.reported)
      .sort((a, b) => a.reporting_date.localeCompare(b.reporting_date));

    for (const item of sortedUpcoming) {
      if (seen.has(item.entity_tag)) {
        continue;
      }
      seen.add(item.entity_tag);
      uniqueUpcoming.push(item);
      if (uniqueUpcoming.length === ROW_LIMIT) {
        break;
      }
    }

    return uniqueUpcoming;
  }, [earnings.data]);

  const { earningsInsightByEarningsId, isEarningsInsightsLoading } =
    useEarningsInsightsByEarningsId({
      earningsIds: upcomingItems?.map(item => item.id) ?? [],
      isEnabled: !earnings.isLoading,
    });

  const data = useMemo(() => {
    if (!upcomingItems) {
      return undefined;
    }

    const rankByTag = new Map(
      (basket ?? []).map(c => [c.company, c.relationship.rank])
    );

    return upcomingItems.map(
      (item): PulseUpcomingEarning => ({
        rank: rankByTag.get(item.entity_tag) ?? 0,
        company: toCompanyRef(item.entity_tag),
        earningsItem: item,
        earningsInsight: earningsInsightByEarningsId.get(item.id),
      })
    );
  }, [upcomingItems, basket, earningsInsightByEarningsId]);

  const status =
    isFilterV2 && data?.length === 0
      ? getPulseEarningsStatus(
          periods.data?.periods.find(
            period =>
              period.year === currentQuarter.year &&
              period.quarter === currentQuarter.quarter
          )
        )
      : null;

  return {
    isLoading:
      companies.isLoading ||
      earnings.isLoading ||
      (data?.length === 0 && periods.isLoading) ||
      isEarningsInsightsLoading,
    data,
    status,
  };
};

/**
 * Related financial/macro themes via ontology/v5/intersections (KG-576).
 *
 * Industry→financial|macro intersection edges do not include
 * exposure_direction. Directions come from each related theme's
 * inbound company-theme relations, joined onto the shared companies.
 * Outbound theme-company is a short ranked list and misses the overlap.
 * TODO(KG-733): drop the reverse calls once BE exposes exposure_direction on
 * the forward intersections response (or adds a target_node filter).
 */
export const usePulseRelatedThemes = ({
  themeId,
  relation,
  enabled,
}: {
  themeId: string;
  relation: 'financial' | 'macro';
  enabled: boolean;
}) =>
  useQuery({
    queryKey: [QUERY_KEY_PULSE_RELATED_THEMES, themeId, relation],
    staleTime: getTimeTillEndOfDayMs(),
    enabled,
    queryFn: async ({ signal }): Promise<PulseRelatedTheme[]> => {
      const related = await getGraphIntersections({
        node: themeId,
        nodeType: 'theme',
        targetRelation: relation,
        limit: ROW_LIMIT,
        signal,
      });
      const labelByKey = new Map(
        related.nodes.map(node => [node.key, node.attributes.label])
      );

      if (isV6IntersectionsResponse(related)) {
        return related.intersections.map((intersection, index) => {
          const counts = countDirections(intersection.target_edges ?? []);

          return mapIntersectionToPulseRelatedTheme({
            intersection,
            index,
            name: labelByKey.get(intersection.node) ?? '',
            counts,
            companiesCount: intersection.target_edges?.length ?? 0,
          });
        });
      }

      return Promise.all(
        related.intersections.map(async (intersection, index) => {
          const relatedThemeId = parseGraphNodeKey(intersection.node).rawId;
          const relations = await getGraphRelations({
            nodeType: 'theme',
            rawNodeId: relatedThemeId,
            relationKind: 'theme-company',
            direction: 'outbound',
            signal,
          });
          const sharedCompanyIds = new Set(
            intersection.edges.map(edge => edge.target)
          );
          const counts = countDirections(
            relations.edges.filter(edge => sharedCompanyIds.has(edge.target))
          );

          return mapIntersectionToPulseRelatedTheme({
            intersection,
            index,
            name: labelByKey.get(intersection.node) ?? '',
            counts,
            companiesCount: intersection.edges.length,
          });
        })
      );
    },
  });

const mapIntersectionToPulseRelatedTheme = ({
  intersection,
  index,
  name,
  counts,
  companiesCount,
}: {
  intersection: IntersectionV6 | IntersectionV5;
  index: number;
  name: string;
  counts: { positive: number; negative: number; neutral: number };
  companiesCount: number;
}) => {
  return {
    rank: index + 1,
    themeId: parseGraphNodeKey(intersection.node).rawId,
    name,
    companiesCount,
    positiveCount: counts.positive,
    negativeCount: counts.negative,
    mixedCount: counts.neutral,
  };
};
